import { renderRoot } from 'quartz-core/client';
import Raven from 'raven-js';
import { BrowserRouter as Router } from 'quartz-routing/react-router';
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import registerAllData from 'I18n/init/register/lazy/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import langNav from 'i2l?query=sporks!../lang/modules/academy-nav/en.lyaml';
import langShared from 'i2l?query=sporks!../lang/modules/academy-shared/en.lyaml';
import enviro from 'enviro';

// @ts-expect-error untyped
import createExperimentI18nProvider from 'laboratory-lib/i18n/createExperimentI18nProvider';
import experimentalLang from 'i2l?query=sporks!../lang/laboratory-copy-experiments/en.lyaml';
import { createLaboratoryClient } from './features/experiments';
import SimpleHubPicker from 'hub-pickers-lib/components/SimpleHubPicker';
import PortalIdParser from 'PortalIdParser';
import LearningCenter from './LearningCenter';
// @ts-expect-error untyped
import HublessError from './components/Errors/HublessError';
import ApiError from './components/Errors/ApiError';
import { addUrlParams, replacePortalIdInPath, getUnauthenticatedUserRedirect } from './utils/RoutingUtils.migrated';
import { isHubSpotNativeApp } from 'academy-common-lib/utils/native/nativeAppDetection';
import { getLanguageFromSignup } from './utils/utils.migrated';
import 'I18n/init/data/currency';
import { getUserInfo, getCrossAccounts, getGatesAndScopes } from './features/accounts';
import { parse, stringify } from 'hub-http/helpers/params';
import './features/export/exportSetup';
import { View } from 'foundations-components/components/View';
import UIApplication from 'UIComponents/app/UIApplication';
import { jsx as _jsx } from "react/jsx-runtime";
function initTranslations(experimentsClient, signupLang, user) {
  const standardI18nProvider = createStandardI18nProvider();
  const ExperimentalI18nProvider = createExperimentI18nProvider({
    client: experimentsClient
  });
  const I18nProviders = [[standardI18nProvider, lang], [standardI18nProvider, langNav], [standardI18nProvider, langShared], [ExperimentalI18nProvider, experimentalLang]];
  const I18nRegistrations = I18nProviders.map(([provider, langFile]) => provider.register(langFile));
  I18nProviders.forEach(([I18nProvider]) => {
    I18nProvider.setLocale({
      locale: signupLang || user.locale,
      langEnabled: signupLang ? true : user.languageEnabled
    });
  });
  return initAppTranslations([...I18nRegistrations, registerAllData(standardI18nProvider)]);
}
export default class Application {
  start() {
    if (window.jasmine) {
      return;
    }
    if (isHubSpotNativeApp) {
      this.mobileAppStyleEl = document.createElement('style');
      this.mobileAppStyleEl.id = 'hs-nav-hide-style-mobile-app';
      this.mobileAppStyleEl.innerHTML = '#hs-nav-v4, #chat-widget { display: none; }';
      document.getElementsByTagName('head')[0].appendChild(this.mobileAppStyleEl);
    }
    const portalIdFromUrl = PortalIdParser.get();
    window.hubspot.portal = {
      id: portalIdFromUrl
    };

    // Switch 'lang' param to 'language' -  ONLY if we do not already have a language param
    const switchLangParamForLanguage = () => {
      const params = parse(window.location.search.slice(1));
      if (!params['language']) {
        Object.keys(params).forEach(param => {
          if (param === 'lang' && params[param] !== 'undefined') {
            params['language'] = params[param].toUpperCase().split('-')[0];
            delete params[param];
            window.location.replace(`${window.location.pathname}?${stringify(params)}`);
          }
        });
      } else {
        // We only support base languages (EN, PT, ES) and not sublanguage codes (EN-US, PT-BR)
        const splitLanguage = params['language'].split('-');
        if (splitLanguage.length > 1) {
          params['language'] = splitLanguage[0].toUpperCase();
          window.location.replace(`${window.location.pathname}?${stringify(params)}`);
        }
      }
    };
    switchLangParamForLanguage();
    const init = Promise.all([getUserInfo(), getCrossAccounts(), getGatesAndScopes()]);
    const signupLang = getLanguageFromSignup();
    init.then(([{
      user,
      actingUserId
    }, {
      accounts
    }, {
      gates,
      scopes
    }]) => {
      this.redirectToImpersonationSplash(actingUserId);
      const experimentsClient = createLaboratoryClient({
        userId: user.id
      });
      Raven.setUserContext({
        id: user.id.toString(),
        email: user.emails[0]
      });
      const userHasAccessToPortal = accounts.find(account => account.id === portalIdFromUrl);
      if (portalIdFromUrl && userHasAccessToPortal !== null && userHasAccessToPortal !== void 0 && userHasAccessToPortal.hublet && userHasAccessToPortal.hublet !== enviro.getHublet()) {
        window.location.replace(replacePortalIdInPath({
          portalId: portalIdFromUrl,
          hublet: userHasAccessToPortal.hublet
        }));
        return;
      }
      const langAvailable = initTranslations(experimentsClient, signupLang, user);
      langAvailable.then(() => {
        if (accounts.length === 1) {
          if (accounts[0].id === portalIdFromUrl) {
            this.startRouter(portalIdFromUrl, user, gates, scopes);
          } else {
            window.location.replace(replacePortalIdInPath({
              portalId: accounts[0].id,
              hublet: accounts[0].hublet
            }));
          }
        } else if (!accounts.length) {
          const userEmail = user.emails && user.emails[0];
          renderRoot( /*#__PURE__*/_jsx(UIApplication, {
            name: "LearningCenterUI",
            children: /*#__PURE__*/_jsx(HublessError, {
              email: userEmail,
              firstName: user.firstName
            })
          }));
        } else if (!portalIdFromUrl || !userHasAccessToPortal) {
          if (portalIdFromUrl) {
            // redirect to academy-profile the user does not have access to the current portal
            const profileMatch = window.location.pathname.match(/\/academy\/\d+\/profile\/(\d+)/);
            if (profileMatch) {
              const userId = profileMatch[1];
              window.location.replace(`/academy-profile/member/${userId}`);
              return;
            }
            const firstAccountHublet = accounts[0] && accounts[0].hublet;
            window.location.replace(addUrlParams(replacePortalIdInPath({
              portalId: 0,
              hublet: firstAccountHublet
            }), {
              rpid: portalIdFromUrl === null || portalIdFromUrl === void 0 ? void 0 : portalIdFromUrl.toString() // 'Referrer portal id' for viral tracking
            }));
            return;
          } else {
            renderRoot( /*#__PURE__*/_jsx(UIApplication, {
              name: "LearningCenterUI",
              children: /*#__PURE__*/_jsx(View, {
                "data-test-id": "simple-hub-picker",
                marginBlock: "700",
                marginInline: "auto",
                maxInlineSize: "800px",
                children: /*#__PURE__*/_jsx(SimpleHubPicker, {
                  redirectTo: portalId => replacePortalIdInPath({
                    portalId,
                    hublet: accounts.find(account => account.id === portalId).hublet
                  })
                })
              })
            }));
          }
        } else {
          this.startRouter(portalIdFromUrl, user, gates, scopes);
        }
      }).catch(error => {
        setTimeout(() => {
          throw error;
        });
      });
    }).catch((error = {}) => {
      console.error(error);
      if (error && error.status === 401) {
        window.location.href = getUnauthenticatedUserRedirect(window.location.href);
      } else {
        const standardI18nProvider = createStandardI18nProvider();
        const i18nProviders = [[standardI18nProvider, lang], [standardI18nProvider, langNav], [standardI18nProvider, langShared]];

        // Set locale + timezone data for each provider
        i18nProviders.forEach(([I18nProvider]) => {
          I18nProvider.setLocale({});
        });
        const I18nRegistrations = i18nProviders.map(([provider, langFile]) => provider.register(langFile));
        const langAvailable = initAppTranslations([...I18nRegistrations, registerAllData(standardI18nProvider)]);
        langAvailable.then(() => {
          renderRoot( /*#__PURE__*/_jsx(UIApplication, {
            name: "LearningCenterUI",
            children: /*#__PURE__*/_jsx(Router, {
              basename: "academy",
              children: /*#__PURE__*/_jsx(ApiError, {
                error: {
                  status: error && error.status
                }
              })
            })
          }));
        }).catch(err => {
          setTimeout(() => {
            throw err;
          });
        });
      }
    });
  }
  startRouter(portalId, user, gates, scopes) {
    const v3Nav = document.getElementById('hs-nav-v3');
    if (v3Nav) {
      v3Nav.classList.add('no-bottom-margin');
    }
    renderRoot( /*#__PURE__*/_jsx(UIApplication, {
      id: "learning-center-ui-root",
      name: "LearningCenterUI",
      children: /*#__PURE__*/_jsx(LearningCenter, {
        gates: gates,
        portalId: portalId,
        scopes: scopes,
        user: user
      })
    }));
  }
  redirectToImpersonationSplash(actingUserId) {
    if (actingUserId) {
      window.location.href = '/login-as-error';
    }
  }
}